// Client for the per-field translation workspace (/api/translations on BCC.ADMIN.API).
//
// IMPORTANT: goes through the shared `axios` instance (NOT raw fetch) so the Auth0AxiosBridge
// request interceptor attaches the admin Bearer token : a raw fetch is invisible to that
// interceptor and 401s when BYPASS_AUTH is off.
import axios from "axios";

export interface TranslationOverviewItem {
	schemaKey: string;
	slug: string;
	contentId: string;
	title: string;
	ok: number;
	stale: number;
	missing: number;
	manual: number;
	total: number;
}

export interface TranslationFieldStatus {
	path: string;
	label: string;
	sourceText: string;
	translatedText?: string | null;
	status: "missing" | "stale" | "ok";
	isManual: boolean;
	translatedAt?: string | null;
}

export interface TranslationStatusResponse {
	schemaKey: string;
	slug: string;
	contentId: string;
	title: string;
	sourceLanguage: string;
	targetLanguage: string;
	fields: TranslationFieldStatus[];
}

export interface TranslateFieldsResult {
	success: boolean;
	errorMessage?: string | null;
	translatedCount: number;
	translatedPaths: string[];
}

/** Coverage aggregates across the WHOLE corpus (independent of paging/filtering). */
export interface TranslationOverviewSummary {
	totalDocs: number;
	translatedDocs: number;
	attentionDocs: number;
	ok: number;
	stale: number;
	missing: number;
	manual: number;
	totalFields: number;
}

export interface TranslationOverviewTypeCount {
	schemaKey: string;
	count: number;
}

/** One server-side page of overview rows + corpus-wide summary/type counts. */
export interface TranslationOverviewPage {
	items: TranslationOverviewItem[];
	page: number;
	pageSize: number;
	filteredTotal: number;
	summary: TranslationOverviewSummary;
	types: TranslationOverviewTypeCount[];
}

export interface OverviewQuery {
	page?: number;
	pageSize?: number;
	q?: string;
	schemaKey?: string;
	status?: "all" | "attention" | "done";
}

const apiBase = (process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

/**
 * One page of the overview. Filtering, searching and slicing happen server-side : the corpus
 * is thousands of documents and shipping it whole froze the tab.
 */
export async function getOverviewPage(language: string, query: OverviewQuery = {}): Promise<TranslationOverviewPage> {
	const r = await axios.get<TranslationOverviewPage>(`${apiBase}/api/translations/overview`, {
		params: {
			language,
			page: query.page ?? 1,
			pageSize: query.pageSize ?? 50,
			q: query.q?.trim() || undefined,
			schemaKey: query.schemaKey || undefined,
			status: query.status && query.status !== "all" ? query.status : undefined
		},
		withCredentials: true
	});
	return r.data;
}

/** Corpus-wide aggregates only : what the dashboard coverage widget needs. */
export async function getOverviewSummary(language: string): Promise<TranslationOverviewSummary> {
	const r = await axios.get<TranslationOverviewSummary>(`${apiBase}/api/translations/overview/summary`, {
		params: { language },
		withCredentials: true
	});
	return r.data;
}

export async function getStatus(
	schemaKey: string,
	slug: string,
	language: string
): Promise<TranslationStatusResponse> {
	const r = await axios.get<TranslationStatusResponse>(
		`${apiBase}/api/translations/${encodeURIComponent(schemaKey)}/status`,
		{ params: { slug, language }, withCredentials: true }
	);
	return r.data;
}

/** Batch AI translation. `paths` empty/undefined = the default scope (missing + stale only). */
export async function translateFields(
	schemaKey: string,
	slug: string,
	language: string,
	paths?: string[]
): Promise<TranslateFieldsResult> {
	const r = await axios.post<TranslateFieldsResult>(
		`${apiBase}/api/translations/${encodeURIComponent(schemaKey)}/translate`,
		{ paths: paths ?? null },
		{ params: { slug, language }, withCredentials: true }
	);
	return r.data;
}

/**
 * Republishes one content document so the per-language artifacts are re-materialized on the CDN.
 * The language segment is always the SOURCE language: the deploy pipeline fans out to every
 * enabled target language itself (FR fallback for untranslated fields).
 */
export async function deployContent(
	schemaKey: string,
	slug: string,
	target: "preview" | "production"
): Promise<void> {
	await axios.post(
		`${apiBase}/api/deployment/${encodeURIComponent(schemaKey)}/fr/deploy/${target}`,
		null,
		{ params: slug ? { slug } : undefined, withCredentials: true }
	);
}

/** Saves a hand-edited translation for one field (protected from AI batches). */
export async function saveField(
	schemaKey: string,
	slug: string,
	language: string,
	path: string,
	value: string
): Promise<void> {
	await axios.put(
		`${apiBase}/api/translations/${encodeURIComponent(schemaKey)}/field`,
		{ path, value },
		{ params: { slug, language }, withCredentials: true }
	);
}
